import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle2, 
  FileText, 
  Database, 
  TrendingUp,
  Shield,
  Clock,
  Target,
  Home,
  Calculator,
  Zap
} from 'lucide-react';

const DigitaleBuchhaltung = () => {
  return (
    <div className="page-container">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Target className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Daniel Leismann</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <Home className="h-4 w-4 mr-2" />
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-accent">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">Digitale Buchhaltung</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Banner Image */}
          <div className="mb-8 rounded-xl overflow-hidden" style={{maxHeight: '200px'}}>
            <img 
              src="https://images.pexels.com/photos/6863332/pexels-photo-6863332.jpeg" 
              alt="Digitale Buchhaltung" 
              className="w-full h-48 object-cover"
            />
          </div>
          
          {/* Content Grid */}
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <h1 className="service-page-title">
                Digitale Buchhaltung optimieren – LexOffice, DATEV, Addison
              </h1>
              <p className="service-page-subtitle">
                Von LexOffice über DATEV bis Addison – ich digitalisiere Ihre Buchhaltungsprozesse, automatisiere Rechnungsstellung und schaffe nahtlose Schnittstellen zwischen allen Systemen. Transparente Kostenersparnis durch weniger manuelle Arbeit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/#kontakt">
                  <Button size="lg" className="cta-button-large">
                    Kostenlose Erstanalyse
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Keine Vorkosten</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Messbare Einsparungen</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Weniger manuelle Arbeit</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-1">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg" 
                  alt="Daniel Leismann" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hauptleistungen */}
      <section className="section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12">Was ich für Ihre Buchhaltung optimiere</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <FileText className="h-6 w-6" />
                </div>
                <h3 className="service-title">LexOffice Integration</h3>
                <p className="service-description">
                  Automatisierte Rechnungserstellung, Bankanbindung, DATEV-Export und Schnittstellen zu Ihren Systemen.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Database className="h-6 w-6" />
                </div>
                <h3 className="service-title">DATEV Anbindung</h3>
                <p className="service-description">
                  Automatischer Datenexport, Buchungslogik, Steuerberater-Schnittstelle und Fehlerreduktion.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Calculator className="h-6 w-6" />
                </div>
                <h3 className="service-title">Addison Optimierung</h3>
                <p className="service-description">
                  Prozessautomatisierung, Reports, Schnittstellen und Workflow-Optimierung für Ihr ERP-System.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Zap className="h-6 w-6" />
                </div>
                <h3 className="service-title">Automatisierung</h3>
                <p className="service-description">
                  Rechnungszuordnung, Belegerkennung, automatische Buchungen und KI-gestützte Prozesse.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Shield className="h-6 w-6" />
                </div>
                <h3 className="service-title">Compliance & Sicherheit</h3>
                <p className="service-description">
                  GoBD-konforme Archivierung, Revisionssicherheit und Datenschutz nach DSGVO.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <h3 className="service-title">Reporting & Analytics</h3>
                <p className="service-description">
                  Live-Dashboards, Liquiditätsplanung, Kostenstellen-Auswertung und Forecasting.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Erfolgsbeispiel */}
      <section className="visual-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title-dark">Typische Einsparung</h2>
          <div className="text-6xl font-bold text-accent my-8">12h / Woche</div>
          <p className="text-xl text-gray-300">
            Durch automatisierte LexOffice-/DATEV-Flows sparen Unternehmen durchschnittlich 12 Stunden Buchhaltungsaufwand pro Woche ein.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title">Bereit für eine effizientere Buchhaltung?</h2>
          <p className="section-subtitle mb-8">
            Lassen Sie uns gemeinsam analysieren, wo Ihre Buchhaltung Zeit und Geld verliert.
          </p>
          <Link to="/#kontakt">
            <Button size="lg" className="cta-button-large">
              Jetzt kostenlose Erstanalyse anfragen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Daniel Leismann. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DigitaleBuchhaltung;